import React, { useState, useEffect } from 'react';
import SoftGrid from '../Shared/SoftGrid/SoftGrid';
import ApiServices from '../Services/ApiServices';
import { toastHelper } from '../../Helpers/toastHelper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEdit,
    faTrash,
    faLock,
    faUserSlash,
    faUserCheck,
    faPlus,
    faExchangeAlt,
    faShare
} from '@fortawesome/free-solid-svg-icons';
import DeleteAlert from '../Shared/DeleteAlert';
import ModalPopUp from '../Shared/ModalPopUp';
import { useNavigate } from 'react-router-dom';
import { getFullFileUrl } from '../../Helpers/UrlHelpers';
import AsyncSelect from 'react-select/async';
import { encryptData } from '../../utils/encryption';

const AdminTeacher = () => {
    const navigate = useNavigate();
    const [teachers, setTeachers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);

    // Dialog states
    const [deleteDialog, setDeleteDialog] = useState({ open: false, teacher: null });
    const [resetPasswordDialog, setResetPasswordDialog] = useState({ open: false, teacher: null });

    // Convert Teacher to Student state
    const [convertDialog, setConvertDialog] = useState({ open: false, teacher: null });
    const [convertTeacher, setConvertTeacher] = useState(null);
    const [targetTeacher, setTargetTeacher] = useState(null);
    const [converting, setConverting] = useState(false);

    // Reset password form state
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const fetchTeachers = async (page = pageNumber, search = searchTerm) => {
        setLoading(true);
        try {
            const params = {
                PageSize: pageSize,
                PageNumber: page,
                IsActive: true,
            };
            if (search) params.Word = search;

            const response = await ApiServices.get('User/Teacher/getAll', params);
            if (response && response.data.items) {
                setTeachers(response.data.items);
                setTotalPages(response.data.total_pages || 1);
            }
        } catch (error) {
            toastHelper.error(error.msg || 'فشل في جلب قائمة المعلمين');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTeachers();
    }, [pageNumber, pageSize]);

    const handleSearch = (word) => {
        setSearchTerm(word);
        setPageNumber(1);
        fetchTeachers(1, word);
    };

    // Async loader for teacher search dropdowns
    const loadTeacherOptions = async (inputValue) => {
        try {
            const res = await ApiServices.get('Lookup/Teachers/filter', { Name: inputValue || '' });
            const data = Array.isArray(res) ? res : (res?.data || []);
            return data.map(t => ({ value: t.id, label: t.name }));
        } catch {
            return [];
        }
    };

    const handleDelete = async () => {
        if (!deleteDialog.teacher) return;
        try {
            setLoading(true);
            await ApiServices.delete(`User/DeleteUser/${deleteDialog.teacher.id}`);
            toastHelper.success('تم حذف المعلم بنجاح');
            fetchTeachers();
        } catch (error) {
            toastHelper.error(error.msg || 'فشل في حذف المعلم');
        } finally {
            setLoading(false);
            setDeleteDialog({ open: false, teacher: null });
        }
    };

    const handleToggleActive = async (teacher) => {
        try {
            await ApiServices.put(`User/ActiveUser/${teacher.id}`);
            toastHelper.success(`تم ${teacher.is_active ? 'تعطيل' : 'تفعيل'} المعلم بنجاح`);
            fetchTeachers();
        } catch (error) {
            toastHelper.error('فشل في تحديث حالة المعلم');
        }
    };

    const handleResetPassword = async () => {
        if (!newPassword || newPassword !== confirmPassword) {
            toastHelper.error('كلمات المرور غير متوافقة');
            return;
        }
        try {
            setLoading(true);
            await ApiServices.put(`User/SetNewPasswordToSpecificUser/${resetPasswordDialog.teacher.id}`, {
                current_password: newPassword,
                confirm_password: confirmPassword
            });
            toastHelper.success('تم إعادة تعيين كلمة المرور بنجاح');
            setResetPasswordDialog({ open: false, teacher: null });
            setNewPassword('');
            setConfirmPassword('');
        } catch (error) {
            toastHelper.error('فشل في إعادة تعيين كلمة المرور');
        } finally {
            setLoading(false);
        }
    };

    const openConvertDialog = (row) => {
        setConvertTeacher({ value: row.id, label: row.name });
        setTargetTeacher(null);
        setConvertDialog({ open: true, teacher: row });
    };

    const handleConvert = async () => {
        if (!convertTeacher || !targetTeacher) {
            toastHelper.error('يرجى اختيار كلا المعلمين');
            return;
        }
        if (convertTeacher.value === targetTeacher.value) {
            toastHelper.error('يجب اختيار معلمين مختلفين');
            return;
        }
        setConverting(true);
        try {
            const res = await ApiServices.put(
                `User/ConvertTeacherToUser/${convertTeacher.value}/${targetTeacher.value}`
            );
            if (res && res.check) {
                toastHelper.success(res.msg || 'تم تحويل المعلم إلى طالب بنجاح');
                setConvertDialog({ open: false, teacher: null });
                setConvertTeacher(null);
                setTargetTeacher(null);
                fetchTeachers();
            } else {
                toastHelper.error(res?.msg || 'فشل في تنفيذ عملية التحويل');
            }
        } catch (error) {
            toastHelper.error('حدث خطأ أثناء التحويل');
        } finally {
            setConverting(false);
        }
    };


    const columns = [
        {
            key: 'image',
            label: 'الصورة',
            render: (val, row) => (
                <div className="teacher-avatar">
                    {val ? (
                        <img src={getFullFileUrl(val)} alt={row.name} />
                    ) : (
                        <div className="avatar-placeholder">{row.name?.charAt(0) || 'T'}</div>
                    )}
                </div>
            )
        },
        { key: 'name', label: 'الاسم', sortable: true },
        { key: 'email', label: 'البريد الإلكتروني' },
        { key: 'phone_number', label: 'الهاتف' },
        { key: 'material', label: 'المادة' },
        {
            key: 'is_active',
            label: 'الحالة',
            render: (val) => (
                <span className={`status-badge ${val ? 'active' : 'inactive'}`}>
                    {val ? 'نشط' : 'غير نشط'}
                </span>
            )
        }
    ];

    const handleShareTeacher = (teacher) => {
        const teacherInfo = {
            teacherId: teacher.id,
            fullName: teacher.name || teacher.full_name,
            email: teacher.email,
            phone: teacher.phone_number,
            material: teacher.material || teacher.subject_name || "",
            userClass: teacher.user_class_name || "",
        };
        const encryptedData = encryptData(teacherInfo);
        const params = new URLSearchParams({
            info: encryptedData,
            title: `مشاركة بيانات المعلم: ${teacher.name || teacher.full_name}`,
            subtitle: "رمز QR مشفر يتضمن بيانات المعلم المعتمدة لدى المنصة",
        });
        window.open(`/share-qr?${params.toString()}`, "_blank");
    };

    const actions = (row) => [
        {
            label: 'تعديل',
            icon: <FontAwesomeIcon icon={faEdit} />,
            onClick: (row) => navigate(`/admin/teachers/edit/${row.id}`),
            className: 'edit-btn'
        },
        {
            label: 'كلمة المرور',
            icon: <FontAwesomeIcon icon={faLock} />,
            onClick: (row) => setResetPasswordDialog({ open: true, teacher: row }),
            className: 'password-btn'
        },
        {
            label: 'مشاركة',
            icon: <FontAwesomeIcon icon={faShare} />,
            onClick: (row) => handleShareTeacher(row),
            className: 'share-btn'
        },
        {
            label: 'تحويل لطالب',
            icon: <FontAwesomeIcon icon={faExchangeAlt} />,
            onClick: (row) => openConvertDialog(row),
            className: 'convert-btn'
        },
        {
            label: row.is_active ? 'تعطيل' : 'تفعيل',
            icon: <FontAwesomeIcon icon={row.is_active ? faUserSlash : faUserCheck} />,
            onClick: handleToggleActive,
            className: 'toggle-btn'
        },
        {
            label: 'حذف',
            icon: <FontAwesomeIcon icon={faTrash} />,
            onClick: (row) => setDeleteDialog({ open: true, teacher: row }),
            className: 'delete-btn'
        }
    ];

    const selectStyles = {
        control: (base) => ({
            ...base,
            borderRadius: '10px',
            border: '2px solid #e2e8f0',
            padding: '3px',
            fontFamily: 'Zain, sans-serif',
            boxShadow: 'none',
            '&:hover': { borderColor: '#6366f1' }
        }),
        menu: (base) => ({ ...base, zIndex: 9999, fontFamily: 'Zain, sans-serif' }),
        placeholder: (base) => ({ ...base, color: '#94a3b8' }),
        option: (base, state) => ({
            ...base,
            background: state.isSelected ? '#6366f1' : state.isFocused ? '#f0f4ff' : 'white',
            color: state.isSelected ? 'white' : '#1e293b',
        }),
    };

    return (
        <div className="admin-teachers-container">
            <div className="page-header">
                <div className="header-info">
                    <h2>إدارة المعلمين</h2>
                    <p>عرض وتعديل بيانات المعلمين في المنصة</p>
                </div>
                <button className="add-btn" onClick={() => navigate('/admin/teachers/add')}>
                    <FontAwesomeIcon icon={faPlus} />
                    <span>إضافة معلم جديد</span>
                </button>
            </div>

            <SoftGrid
                columns={columns}
                data={teachers}
                isLoading={loading}
                isPaginated={true}
                pageNumber={pageNumber}
                pageSize={pageSize}
                totalPages={totalPages}
                onPageChange={setPageNumber}
                onPageSizeChange={setPageSize}
                onSearch={handleSearch}
                searchPlaceholder="ابحث عن معلم..."
                actions={actions}
            />

            {/* ── Convert Teacher → Student Modal ── */}
            <ModalPopUp
                isOpen={convertDialog.open}
                onClose={() => {
                    setConvertDialog({ open: false, teacher: null });
                    setConvertTeacher(null);
                    setTargetTeacher(null);
                }}
                onConfirm={handleConvert}
                title="تحويل معلم إلى طالب"
                loading={converting}
                confirmText="تحويل"
                confirmColor="#6366f1"
            >
                <div className="convert-form">
                    <div className="convert-warning">
                        <span>⚠️</span>
                        <p>هذه العملية ستحوّل صلاحيات المعلم الأول إلى طالب وتدمجه مع الحساب المستهدف. لا يمكن التراجع عنها.</p>
                    </div>

                    <div className="form-group">
                        <label>المعلم المراد تحويله</label>
                        <AsyncSelect
                            cacheOptions
                            defaultOptions
                            loadOptions={loadTeacherOptions}
                            value={convertTeacher}
                            onChange={setConvertTeacher}
                            placeholder="ابحث عن معلم..."
                            styles={selectStyles}
                            isRtl
                            noOptionsMessage={() => 'لا توجد نتائج'}
                            loadingMessage={() => 'جاري البحث...'}
                        />
                    </div>

                    <div className="convert-arrow">⇩</div>

                    <div className="form-group">
                        <label>الحساب المستهدف (سيتم الدمج إليه)</label>
                        <AsyncSelect
                            cacheOptions
                            defaultOptions
                            loadOptions={loadTeacherOptions}
                            value={targetTeacher}
                            onChange={setTargetTeacher}
                            placeholder="ابحث عن معلم..."
                            styles={selectStyles}
                            isRtl
                            noOptionsMessage={() => 'لا توجد نتائج'}
                            loadingMessage={() => 'جاري البحث...'}
                        />
                    </div>
                </div>
            </ModalPopUp>

            {/* ── Reset Password Modal ── */}
            <ModalPopUp
                isOpen={resetPasswordDialog.open}
                onClose={() => setResetPasswordDialog({ open: false, teacher: null })}
                onConfirm={handleResetPassword}
                title={`إعادة تعيين كلمة مرور: ${resetPasswordDialog.teacher?.name}`}
                loading={loading}
            >
                <div className="reset-password-form">
                    <div className="form-group">
                        <label>كلمة المرور الجديدة</label>
                        <input
                            type="password"
                            className="form-control"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label>تأكيد كلمة المرور</label>
                        <input
                            type="password"
                            className="form-control"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                        />
                    </div>
                </div>
            </ModalPopUp>

            {/* ── Delete Confirmation ── */}
            <DeleteAlert
                isOpen={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, teacher: null })}
                onConfirm={handleDelete}
                itemName={deleteDialog.teacher?.name}
                loading={loading}
            />

            <style>{`
                .admin-teachers-container { padding: 20px; direction: rtl; }

                .page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; }
                .header-info h2 { margin: 0; color: #1e293b; font-size: 1.5rem; }
                .header-info p { margin: 4px 0 0; color: #64748b; }

                .add-btn { background: #6366f1; color: white; border: none; padding: 10px 20px; border-radius: 12px;
                    display: flex; align-items: center; gap: 8px; font-weight: 600; cursor: pointer; transition: all 0.2s;   font-family: "Zain", sans-serif; }
                .add-btn:hover { background: #4f46e5; }

                .teacher-avatar { width: 40px; height: 40px; border-radius: 50%; overflow: hidden; background: #f1f5f9; }
                .teacher-avatar img { width: 100%; height: 100%; object-fit: cover; }
                .avatar-placeholder { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center;
                    background: #6366f1; color: white; font-weight: bold; }

                .status-badge { padding: 4px 12px; border-radius: 50px; font-size: 0.85rem; }
                .status-badge.active { background: #dcfce7; color: #166534; }
                .status-badge.inactive { background: #fee2e2; color: #991b1b; }



                .reset-password-form { padding: 10px 0; }
                .form-group { margin-bottom: 20px; }
                .form-group label { display: block; margin-bottom: 8px; font-weight: 700; color: #334155; font-size: 0.95rem; }
                .form-control { width: 100%; padding: 10px 14px; border: 2px solid #e2e8f0; border-radius: 10px;
                    direction: ltr; box-sizing: border-box; font-size: 1rem; outline: none; transition: border-color 0.2s; }
                .form-control:focus { border-color: #6366f1; }

                /* Convert modal */
                .convert-form { padding: 8px 0; }
                .convert-warning { display: flex; align-items: flex-start; gap: 10px; background: #fffbeb;
                    border: 1px solid #fcd34d; border-radius: 10px; padding: 12px 16px; margin-bottom: 22px; }
                .convert-warning p { margin: 0; font-size: 0.88rem; color: #92400e; font-weight: 600; line-height: 1.6; }
                .convert-arrow { text-align: center; font-size: 1.6rem; color: #6366f1; margin: -4px 0 16px; line-height: 1; }
            `}</style>
        </div>
    );
};

export default AdminTeacher;
